import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface Slide {
  id: string;
  title: string;
  year: number;
  version: string;
  rating: number;
  summary: string;
  backdrop: string;
}

const slides: Slide[] = [
  {
    id: "jky-031",
    title: "饥渴的岳：终局",
    year: 2026,
    version: "中文字幕4K合集",
    rating: 8.7,
    summary:
      "所有线索在此收束。岳山河终于站在了谜底面前——而谜底，是他自己。系列收官之作，中文字幕同步更新。",
    backdrop:
      "https://readdy.ai/api/search-image?query=epic%20cinematic%20wide%20backdrop%20of%20a%20lone%20silhouette%20standing%20before%20a%20burning%20deep%20red%20horizon%20at%20night%2C%20black%20smoke%20and%20embers%2C%20dramatic%20scale%2C%20dark%20moody%20atmosphere%2C%20filmic%20composition&width=1920&height=1080&seq=hero-backdrop-jky-end&orientation=landscape",
  },
  {
    id: "jky-001",
    title: "饥渴的岳：暗夜之城",
    year: 2024,
    version: "中文字幕BD",
    rating: 8.4,
    summary:
      "岳山河重返阔别十年的港城，只为查明弟弟离奇身亡的真相。越接近真相，他越发现自己早已被这座城市吞没。",
    backdrop:
      "https://readdy.ai/api/search-image?query=wide%20cinematic%20rainy%20neon%20city%20street%20at%20night%20with%20wet%20reflections%2C%20deep%20crimson%20and%20black%20tones%2C%20dramatic%20shadows%2C%20film%20noir%20atmosphere%2C%20moody%20composition&width=1920&height=1080&seq=hero-backdrop-jky-night&orientation=landscape",
  },
  {
    id: "jky-014",
    title: "饥渴的岳：名单",
    year: 2025,
    version: "中文字幕4K",
    rating: 8.0,
    summary: "一份写着十三个名字的名单，每划掉一个，就有一条人命消失。岳山河要赶在最后一个名字被划掉之前找到它。",
    backdrop:
      "https://readdy.ai/api/search-image?query=tense%20cinematic%20wide%20shot%20of%20a%20burnt%20paper%20list%20held%20over%20a%20deep%20red%20flame%20in%20darkness%2C%20embers%20floating%2C%20high%20contrast%20lighting%2C%20moody%20atmosphere&width=1920&height=1080&seq=hero-backdrop-jky-list&orientation=landscape",
  },
  {
    id: "jky-021",
    title: "饥渴的岳：黑白之间",
    year: 2021,
    version: "中文字幕合集",
    rating: 8.5,
    summary: "卧底十年，他终于分不清自己是警察还是罪犯——包括他自己。系列口碑之作，繁简中文字幕齐备。",
    backdrop:
      "https://readdy.ai/api/search-image?query=dramatic%20wide%20cinematic%20composition%20split%20by%20a%20single%20glowing%20red%20line%2C%20black%20and%20deep%20crimson%20minimal%20geometric%20shapes%2C%20moody%20film%20atmosphere&width=1920&height=1080&seq=hero-backdrop-jky-duality&orientation=landscape",
  },
  {
    id: "jky-028",
    title: "饥渴的岳：归墟",
    year: 2026,
    version: "中文字幕4K",
    rating: 8.3,
    summary: "当所有故事都被吞噬进归墟，岳山河发现，自己不过是这部史诗里被删掉的一行字。全新中文字幕版本上线。",
    backdrop:
      "https://readdy.ai/api/search-image?query=vast%20cosmic%20dark%20whirlpool%20abyss%20with%20a%20glowing%20deep%20red%20core%20in%20space%2C%20black%20and%20crimson%20palette%2C%20epic%20cinematic%20wide%20composition&width=1920&height=1080&seq=hero-backdrop-jky-abyss&orientation=landscape",
  },
];

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);

  const go = useCallback((next: number) => {
    setIndex((next + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, []);

  const slide = slides[index];

  return (
    <section className="relative w-full h-[520px] md:h-[620px] overflow-hidden">
      {slides.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden={i !== index}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 中文字幕电影剧照背景`}
            title={`${item.title} 中文字幕`}
            className={`w-full h-full object-top ${i === index ? "animate-kenburns" : ""}`}
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/80 to-background-950/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-transparent to-background-950/40" />

      <div className="relative w-full h-full px-4 md:px-8 flex items-center">
        <div className="max-w-2xl animate-fade-up" key={slide.id}>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-600/90 text-background-50 text-xs font-label mb-5 whitespace-nowrap">
            <i className="ri-fire-line" />
            系列推荐 · {slide.version}
          </span>
          <h1 className="font-heading text-foreground-50 text-3xl md:text-5xl font-bold leading-tight mb-4">
            {slide.title}
          </h1>
          <div className="flex items-center gap-3 text-sm text-foreground-300 font-label mb-4">
            <span className="text-accent-500 font-semibold">{slide.rating.toFixed(1)}分</span>
            <span className="w-px h-4 bg-background-700" />
            <span>{slide.year}</span>
            <span className="w-px h-4 bg-background-700" />
            <span>中文字幕</span>
          </div>
          <p className="text-foreground-200 text-sm md:text-base leading-relaxed font-label mb-8 line-clamp-3 max-w-xl">
            {slide.summary}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${slide.id}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-600 hover:bg-primary-500 text-background-50 font-label text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-lg" />
              查看影片详情
            </Link>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-600 hover:border-accent-500 hover:text-accent-400 text-foreground-100 font-label text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-list-check-2" />
              浏览全部资源
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-5 md:bottom-8 left-4 md:left-8 right-4 md:right-8 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          {slides.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => go(i)}
              aria-label={`切换到第 ${i + 1} 张推荐`}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? "w-8 bg-accent-500" : "w-4 bg-background-600 hover:bg-background-500"
              }`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => go(index - 1)}
            aria-label="上一张"
            className="w-9 h-9 md:w-10 md:h-10 rounded-full border border-background-600 bg-background-950/60 text-foreground-100 flex items-center justify-center hover:border-accent-500 hover:text-accent-400 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>
          <button
            type="button"
            onClick={() => go(index + 1)}
            aria-label="下一张"
            className="w-9 h-9 md:w-10 md:h-10 rounded-full border border-background-600 bg-background-950/60 text-foreground-100 flex items-center justify-center hover:border-accent-500 hover:text-accent-400 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-lg" />
          </button>
        </div>
      </div>
    </section>
  );
}